import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import appConfig from "../../consumingApi/services/appConfig";
import LogisticService from "@LogisticService";
import { senninhaColors } from "../../utils/common";

const BASE_URL = appConfig.logisticsUrl;
const REDUCER_NAME = "pinsSenninha";
const GET_PINS = "pinsSenninha/getPins";
const GET_PINS_STONE_ENTREGA = "pinsSenninha/getPinsStoneEntrega";
const GET_HUNTER = "pinsSenninha/getPinsHunter";
const GET_HUNTER_STONE = "pinsSenninha/getPinsHunterStone";
const GET_SERVICE_DETAILS = "pinsSenninha/serviceDetails";

const initialState = {
  pins: [],
  filteredPins: [],
  pinsStatus: "idle",
  serviceDetails: {},
  serviceDetailsStatus: "idle",
  pinZeroCoordenadas: { lat: 0.0, lng: 0.0 },
  center: { lat: 0.0, lng: 0.0 },
  zoom: 10,
  filters: [],
  openDrawer: true,
  leftOpenDrawer: false,
  mapWorkflow: "infoDrawer",
  hunterTonStatus: "idle",
  hunterStoneStatus: "idle",
  stoneEntregaStatus: "idle",
  bouncedPin: "",
  pinsError: false,
};

const STATIC_ANGEL_MAP = {
  "Vencidas (Não alocadas)": { color: "#E6171E", name: "calendar" },
  "Vencem hoje (Não alocadas)": { color: "#F38428", name: "notification" },
  "Vencimento futuros (Não alocadas)": { color: "#0057D9", name: "calendar" },
  Baixadas: { color: "#0BA749", name: "calendar-check" },
  "Comercial Redirect": { color: "#E6B91A", name: "device-card-machine" },
  DELIVERY: { color: "#767F8D", name: "transport-motorcycle" },
  LOGGI: { color: "#00BAFF", name: "transport-motorcycle" },
  "OS Noturna": { color: "#000000", name: "moon" },
};

const getRandomColor = () =>
  senninhaColors[Math.floor(Math.random() * senninhaColors.length)];

const getColorAndIcon = (angel, angelColors) => {
  if (STATIC_ANGEL_MAP[angel]) {
    return STATIC_ANGEL_MAP[angel];
  }

  if (!angelColors[angel]) {
    const usedColors = new Set(Object.values(angelColors));
    const unusedColors = senninhaColors.filter((c) => !usedColors.has(c));

    let color;
    if (unusedColors.length > 0) {
      color = unusedColors[Math.floor(Math.random() * unusedColors.length)];
    } else {
      color = getRandomColor();
    }

    angelColors[angel] = color;
    return { color: color, name: "avatar" };
  }
  return { color: angelColors[angel], name: "avatar" };
};

const setAssociatedPinsAny = (pins) => {
  const entregaMap = {};

  pins.forEach((pin) => {
    if (pin?.servico?.toLowerCase() === "entrega") {
      const entregaIds = Array.isArray(pin.id_atendimento)
        ? pin.id_atendimento
        : [];
      entregaIds.forEach((id) => {
        if (!entregaMap[id]) entregaMap[id] = [];
        entregaMap[id].push(pin.id);
      });
    }
  });

  return pins.map((item) => {
    if (item?.servico?.toLowerCase() !== "coleta") {
      return item;
    }

    const coletaIds = Array.isArray(item.id_atendimento)
      ? item.id_atendimento
      : [];
    const associatedSet = new Set();

    coletaIds.forEach((id) => {
      if (entregaMap[id]) {
        entregaMap[id].forEach((pinId) => associatedSet.add(pinId));
      }
    });

    return { ...item, associatedPinsIDs: Array.from(associatedSet) };
  });
};

const fixSameCoords = (pins) => {
  const coords = new Map();

  pins.forEach((item) => {
    const key = `${item.lat},${item.lng}`;
    if (!coords.has(key)) {
      coords.set(key, []);
    }
    coords.get(key).push(item);
  });

  coords.forEach((sameCoordPin) => {
    const pinsAmount = sameCoordPin.length;

    if (pinsAmount > 1) {
      const middleIndex = Math.floor(pinsAmount / 2);
      sameCoordPin.forEach((pin, index) => {
        pin.lng += (index - middleIndex) * 0.00008;
      });
    }
  });

  return pins;
};

export const getPins = createAsyncThunk(
  GET_PINS,
  async ({ provider }, { rejectWithValue }) => {
    try {
      const res = await LogisticService.requestWithAuth(
        `${BASE_URL}/atendimento/senninha/rotas/adquirencia/${provider}`
      );
      const angelColors = {};

      const pinsWithStyles =
        res?.data?.map((pin, i) => ({
          id: `adq-${i}`,
          ...pin,
          lat: parseFloat(pin.lat),
          lng: parseFloat(pin.lng),
          stylePin: getColorAndIcon(pin.angel, angelColors),
        })) || [];

      return fixSameCoords(setAssociatedPinsAny(pinsWithStyles));
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data || err.response);
    }
  }
);

export const getPinsStoneEntrega = createAsyncThunk(
  GET_PINS_STONE_ENTREGA,
  async ({ provider }, { rejectWithValue }) => {
    try {
      const res = await LogisticService.requestWithAuth(
        `${BASE_URL}/atendimento/senninha/rotas/stone_entrega/${provider}`
      );
      const angelColors = {};

      const pinsWithStyles =
        res?.data?.map((pin, i) => ({
          id: `se-${i}`,
          ...pin,
          lat: parseFloat(pin.lat),
          lng: parseFloat(pin.lng),
          stylePin: getColorAndIcon(pin.angel, angelColors),
        })) || [];

      return fixSameCoords(setAssociatedPinsAny(pinsWithStyles));
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data || err.response);
    }
  }
);

export const getPinsHunterTon = createAsyncThunk(
  GET_HUNTER,
  async ({ provider }, { rejectWithValue }) => {
    try {
      const res = await LogisticService.requestWithAuth(
        `${BASE_URL}/atendimento/senninha/rotas_0tpv_ton/${provider}`
      );
      return res;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data || err.response);
    }
  }
);

export const getPinsHunterStone = createAsyncThunk(
  GET_HUNTER_STONE,
  async ({ provider }, { rejectWithValue }) => {
    try {
      const res = await LogisticService.requestWithAuth(
        `${BASE_URL}/atendimento/senninha/rotas_0tpv/${provider}`
      );
      return res;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data || err.response);
    }
  }
);

export const getAllPinsData = createAsyncThunk(
  "getAllPinsData",
  async ({ provider }, { dispatch }) => {
    try {
      const results = await Promise.allSettled([
        dispatch(getPins({ provider })),
        dispatch(getPinsHunterTon({ provider })),
        dispatch(getPinsHunterStone({ provider })),
        dispatch(getPinsStoneEntrega({ provider })),
      ]);

      const pinsResult = results[0];
      const hunterTonResult = results[1];
      const hunterStoneResult = results[2];
      const stoneEntregaResult = results[3];

      const normalizeHunterPins = (data) =>
        Array.isArray(data)
          ? data.map((pin) => ({
              ...pin,
              lat: parseFloat(pin.lat),
              lng: parseFloat(pin.lng),
            }))
          : [];

      const failed = (result) =>
        result.status !== "fulfilled" || result.value?.error;

      const payload = {
        pins: !failed(pinsResult) ? pinsResult.value.payload : [],
        hunterTon: !failed(hunterTonResult)
          ? normalizeHunterPins(hunterTonResult.value.payload.data)
          : [],
        hunterStone: !failed(hunterStoneResult)
          ? normalizeHunterPins(hunterStoneResult.value.payload.data)
          : [],
        stoneEntrega: !failed(stoneEntregaResult)
          ? stoneEntregaResult.value.payload
          : [],
        pinsFailed: failed(pinsResult),
        hunterTonFailed: failed(hunterTonResult),
        hunterStoneFailed: failed(hunterStoneResult),
        stoneEntregaFailed: failed(stoneEntregaResult),
      };

      return payload;
    } catch (error) {
      console.error("Failed to fetch data from one or both endpoints:", error);
      return {
        pins: [],
        hunterTon: [],
        hunterStone: [],
        stoneEntrega: [],
        pinsFailed: true,
        hunterTonFailed: true,
        hunterStoneFailed: true,
        stoneEntregaFailed: true,
      };
    }
  },
  {
    condition: (_args, { getState }) =>
      getState().pinsSlice.pinsStatus !== "pending",
  }
);

export const getServiceDetails = createAsyncThunk(
  GET_SERVICE_DETAILS,
  async (
    { id, id_atendimento, provider, servico, id_os },
    { rejectWithValue, dispatch }
  ) => {
    try {
      dispatch(
        setServiceDetails({
          pinDetails: null,
          pinIdentifiers: { id, servico, id_os },
        })
      );

      const res = await LogisticService.requestWithAuth(
        `${BASE_URL}/atendimento/senninha/detalhamento/${provider}/${encodeURIComponent(
          id_atendimento
        )}`
      );

      const pinData = res.data?.find((item) =>
        item.oss?.some((os) =>
          Array.isArray(id_os) ? id_os.includes(os.id_os) : os.id_os === id_os
        )
      );

      return {
        pinDetails: {
          id,
          ...(pinData || res.data?.[0] || {}),
        },
        pinIdentifiers: { id, servico, id_os },
      };
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data || err.response);
    }
  }
);

// Extrai faixas de horário de uma string livre de horario_funcionamento.
// Cobre os formatos reais da base: "10:00 - 17:00", "10h - 17h", "08h às 12h",
// "Das 09:00 as 20:00", "08h30 - 21h30" etc. Ignora ruído (ex: "Horário comercial", ".").
const TIME_RANGE_PATTERN = /(\d{1,2})(?::(\d{2}))?h?\s*(?:-|às|as|a)\s*(\d{1,2})(?::(\d{2}))?h?/gi;

// Manhã: 00h-12h · Tarde: 12h-18h · Noite: 18h-00h
const getIntervals = (fromMinutes, toMinutes) => {
  const periods = [];

  const overlaps = (rangeStart, rangeEnd) =>
    fromMinutes < rangeEnd &&
    (toMinutes > rangeStart || (toMinutes === 0 && rangeEnd === 24 * 60));

  if (overlaps(0, 12 * 60)) periods.push("manha");
  if (overlaps(12 * 60, 18 * 60)) periods.push("tarde");
  if (overlaps(18 * 60, 24 * 60)) periods.push("noite");

  return periods;
};

// `horario_funcionamento` se repete bastante entre OSs (prestadores usam
// horários padronizados). Com filtros rodando sobre milhares de pins a cada
// clique, parsear a mesma string repetidamente via regex é um custo real —
// cacheamos o resultado por string de entrada.
const attendancePeriodsCache = new Map();

const getAttendancePeriodsTime = (horarioFuncionamento) => {
  if (!horarioFuncionamento || typeof horarioFuncionamento !== "string")
    return [];

  const cached = attendancePeriodsCache.get(horarioFuncionamento);
  if (cached) return cached;

  const found = new Set();
  let match;
  TIME_RANGE_PATTERN.lastIndex = 0;
  while ((match = TIME_RANGE_PATTERN.exec(horarioFuncionamento)) !== null) {
    const fromHour = Number(match[1]);
    const fromMin = Number(match[2] ?? 0);
    const toHour = Number(match[3]);
    const toMin = Number(match[4] ?? 0);

    if (fromHour > 23 || toHour > 24 || fromMin > 59 || toMin > 59) continue;

    getIntervals(
      fromHour * 60 + fromMin,
      toHour * 60 + toMin
    ).forEach((period) => found.add(period));
  }

  const result = [...found];
  attendancePeriodsCache.set(horarioFuncionamento, result);
  return result;
};

const filterPinByType = (filter, pin) => {
  if (filter.field === "tecnologias") {
    //array
    const hasFilter = pin[filter.field].filter(
      (value) => value === filter.value
    );
    return hasFilter.length > 0;
  } else if (filter.field === "periodo_atendimento") {
    return pin.oss?.some((os) =>
      getAttendancePeriodsTime(os.horario_funcionamento).includes(filter.value)
    );
  } else if (filter.field === "quantidade_oss") {
    return (pin.oss?.length || 0) === filter.value;
  } else if (filter.field === "deadline_date") {
    //array de objectos
    const hasFilterByDate = pin.oss.map((os) =>
      filter.arrayDates.some((value) => value === os.deadline_date)
    );
    return hasFilterByDate.includes(true);
  } else if (filter.field === "customer_deadline_date") {
    //array de objectos
    const hasFilterByDate = pin.oss.map((os) =>
      filter.arrayClientDates.some(
        (value) => value === os.customer_deadline_date
      )
    );
    return hasFilterByDate.includes(true);
  } else {
    //string
    return pin[filter.field] === filter.value;
  }
};

// Lógica pura do filtro, reaproveitada pelo reducer `applyFilter` e por todo
// reducer que muda `state.filters` (setBuildFilters/clearFilter/clearFilterDate),
// pra manter `filteredPins` sempre consistente sem depender de um dispatch
// manual separado logo em seguida.
const computeFilteredPins = (pins, filters) => {
  const legendaFilters = filters.filter((f) => f.drawer === "legenda");
  const filtrosFilters = filters.filter((f) => f.drawer === "filtro");
  const routeManagementFilters = filters.filter(
    (f) => f.drawer === "gestaoRotas"
  );

  const groupByField = (filters) => {
    return filters.reduce((acc, filter) => {
      if (!acc[filter.field]) {
        acc[filter.field] = [];
      }
      acc[filter.field].push(filter);
      return acc;
    }, {});
  };

  const filterPinsByFieldGroups = (pin, fieldGroups) => {
    return Object.values(fieldGroups).every((filtersGroup) =>
      filtersGroup.some((filter) => filterPinByType(filter, pin))
    );
  };

  const filtrosFieldGroups = groupByField(filtrosFilters);

  let filteredPins = pins.filter((pin) => {
    return legendaFilters.some((filter) => filterPinByType(filter, pin));
  });

  if (filtrosFilters.length > 0) {
    filteredPins = filteredPins.filter((pin) =>
      filterPinsByFieldGroups(pin, filtrosFieldGroups)
    );
  }

  if (routeManagementFilters.length) {
    filteredPins = filteredPins.filter((pin) =>
      routeManagementFilters.some((filter) => filterPinByType(filter, pin))
    );
  }

  return filteredPins;
};

export const pinsSlice = createSlice({
  name: REDUCER_NAME,
  initialState,
  reducers: {
    setCenter: (state, action) => {
      state.center = { ...action.payload.center };
    },
    setZoom: (state, action) => {
      state.zoom = action.payload;
    },
    setBouncePin: (state, action) => {
      state.bouncedPin = action.payload;
    },
    applyFilter: (state) => {
      state.filteredPins = computeFilteredPins(state.pins, state.filters);
    },
    setBuildFilters: (state, action) => {
      const newFilters = action.payload;
      let activeFilters = [...state.filters];
      newFilters.forEach((newFilter) => {
        if (newFilter.field === "deadline_date") {
          activeFilters = activeFilters.filter(
            (filter) => filter.field !== "deadline_date"
          );
        }
        if (newFilter.field === "customer_deadline_date") {
          activeFilters = activeFilters.filter(
            (filter) => filter.field !== "customer_deadline_date"
          );
        }
        const existingFilterIndex = activeFilters.findIndex(
          (filter) =>
            filter.field === newFilter.field && filter.value === newFilter.value
        );
        if (existingFilterIndex === -1) {
          activeFilters.push(newFilter);
        }
      });

      activeFilters = activeFilters.filter((activeFilter) => {
        return newFilters.some(
          (newFilter) =>
            newFilter.field === activeFilter.field &&
            newFilter.value === activeFilter.value
        );
      });

      state.filters = activeFilters;
      state.filteredPins = computeFilteredPins(state.pins, state.filters);
    },
    clearFilter: (state, action) => {
      const typeDrawer = action.payload;
      let filtersCleared = state.filters.filter(
        (value) => value.drawer !== typeDrawer
      );
      state.filters = filtersCleared;
      state.filteredPins = computeFilteredPins(state.pins, state.filters);
    },
    clearFilterDate: (state, action) => {
      const fieldToClear = action.payload;
      let filtersCleared = state.filters.filter(
        (filter) => filter.field !== fieldToClear
      );
      state.filters = filtersCleared;
      state.filteredPins = computeFilteredPins(state.pins, state.filters);
    },
    clearStatus: (state) => {
      state.pinsStatus = "idle";
      state.zoom = 10;
      state.mapWorkflow = "infoDrawer";
      state.openDrawer = false;
      state.filteredPins = [];
      state.pins = [];
    },
    setOpenDrawer: (state, action) => {
      state.openDrawer = action.payload;
    },
    setMapWorkflow: (state, action) => {
      state.mapWorkflow = action.payload;
    },
    setLeftOpenDrawer: (state, action) => {
      state.leftOpenDrawer = action.payload;
    },
    clearHunterPin: (state) => {
      state.filters = state.filters.filter(
        (filter) =>
          filter.value !== "Hunter STONE" &&
          filter.value !== "Hunter STONE (Com ineficiência)" &&
          filter.value !== "Hunter TON" &&
          filter.value !== "Hunter TON (Com ineficiência)"
      );
    },
    setServiceDetails: (state, action) => {
      state.serviceDetails = action.payload;
    },
    clearServiceDetails: (state) => {
      state.serviceDetails = {};
      state.serviceDetailsStatus = "idle";
    },
    manuallyAlocatePin: (state, action) => {
      const { id, angel } = action.payload;
      const colorsTaken = state.pins.map((item) => item.stylePin.color);

      const angelStylePin = state.pins.find((item) => item.angel === angel)
        ?.stylePin || {
        color: senninhaColors.filter(
          (color) => !colorsTaken.includes(color)
        )[0],
        name: "avatar",
      };

      state.pins = current(state.pins).map((item) =>
        item.id === id ? { ...item, angel, stylePin: angelStylePin } : item
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceDetails.pending, (state) => {
        state.serviceDetailsStatus = "pending";
      })
      .addCase(getServiceDetails.fulfilled, (state, action) => {
        state.serviceDetails = action.payload;
        state.serviceDetailsStatus = "fulfilled";
      })
      .addCase(getServiceDetails.rejected, (state) => {
        state.serviceDetailsStatus = "rejected";
      })
      .addCase(getAllPinsData.pending, (state) => {
        state.pinsStatus = "pending";
        state.hunterTonStatus = "pending";
        state.hunterStoneStatus = "pending";
        state.stoneEntregaStatus = "pending";
        state.pinsError = false;
      })
      .addCase(getAllPinsData.fulfilled, (state, action) => {
        const {
          pinsFailed,
          hunterTonFailed,
          hunterStoneFailed,
          stoneEntregaFailed,
        } = action.payload;
        const previousPins = current(state.pins);
        const byPrefix = (prefix) =>
          previousPins.filter((pin) => pin.id?.startsWith(prefix));

        // Se uma fonte falhou, mantém os pins que já estavam no mapa em vez de esvaziar.
        const pins = pinsFailed ? byPrefix("adq-") : action.payload.pins;
        const stoneEntrega = stoneEntregaFailed
          ? byPrefix("se-")
          : action.payload.stoneEntrega;
        // Pins de Hunter não têm `id` próprio (usam `id_atendimento`); atribuímos
        // um id único e prefixado para não colidir com as outras fontes na render.
        const hunterTon = hunterTonFailed
          ? byPrefix("ht-")
          : action.payload.hunterTon.map((pin, i) => ({
              ...pin,
              id: `ht-${i}`,
            }));
        const hunterStone = hunterStoneFailed
          ? byPrefix("hs-")
          : action.payload.hunterStone.map((pin, i) => ({
              ...pin,
              id: `hs-${i}`,
            }));

        const maxVolumePin = [...pins, ...stoneEntrega].reduce(
          (max, pin) =>
            pin.volume_oss > (max?.volume_oss ?? -Infinity) ? pin : max,
          null
        );
        const coords = maxVolumePin
          ? { lat: maxVolumePin.lat, lng: maxVolumePin.lng }
          : { lat: 0, lng: 0 };

        state.pinZeroCoordenadas = coords;
        state.center = coords;
        state.zoom = 10;

        const latPinZeroCoordenadas = () =>
          coords.lat + (Math.random() - 0.5) / 10000;
        const lngPinZeroCoordenadas = () =>
          coords.lng + (Math.random() - 0.5) / 10000;
        const filteredPins = [
          ...pins,
          ...stoneEntrega,
          ...hunterTon,
          ...hunterStone,
        ]
          .filter(
            (pin) =>
              pin.angel == "Vencimento futuros (Não alocadas)" ||
              pin.angel == "Vencidas (Não alocadas)" ||
              pin.angel == "Vencem hoje (Não alocadas)"
          )
          .map((pin) => {
            // Verifica se lat e lng são coordenadas zeradas, se sim altera para que ela vá para o centro do polo
            if (pin.lat === 0 && pin.lng === 0) {
              return {
                ...pin,
                lat: latPinZeroCoordenadas(),
                lng: lngPinZeroCoordenadas(),
              };
            }
            return pin;
          });

        state.pins = [
          ...pins,
          ...stoneEntrega,
          ...hunterTon,
          ...hunterStone,
        ].map((pin) => {
          // Verifica se lat e lng são coordenadas zeradas, se sim altera para que ela vá para o centro do polo
          if (pin.lat === 0 && pin.lng === 0) {
            return {
              ...pin,
              lat: latPinZeroCoordenadas(),
              lng: lngPinZeroCoordenadas(),
            };
          }
          return pin;
        });
        state.filteredPins = filteredPins;
        state.pinsStatus = pinsFailed ? "rejected" : "fulfilled";
        state.hunterTonStatus = hunterTonFailed ? "rejected" : "fulfilled";
        state.hunterStoneStatus = hunterStoneFailed ? "rejected" : "fulfilled";
        state.stoneEntregaStatus = stoneEntregaFailed
          ? "rejected"
          : "fulfilled";
        state.pinsError =
          pinsFailed ||
          hunterTonFailed ||
          hunterStoneFailed ||
          stoneEntregaFailed;
      })
      .addCase(getAllPinsData.rejected, (state) => {
        state.pinsStatus = "rejected";
        state.hunterTonStatus = "rejected";
        state.hunterStoneStatus = "rejected";
        state.stoneEntregaStatus = "rejected";
        state.pinsError = true;
      });
  },
});

export const {
  setCenter,
  setZoom,
  setBouncePin,
  clearStatus,
  applyFilter,
  setOpenDrawer,
  setMapWorkflow,
  setServiceDetails,
  clearServiceDetails,
  clearFilter,
  clearFilterDate,
  setBuildFilters,
  setLeftOpenDrawer,
  manuallyAlocatePin,
  clearHunterPin,
} = pinsSlice.actions;

export const pinsSelector = (state) => state.pinsSlice;

export default pinsSlice.reducer;
